import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';
import { preloadImages } from '../ProductCard/Utils';

/**
 * Generates an IntersectionObserver callback for lazy-loading videos.
 * Loads videos when their wrappers become visible in the viewport.
 * @param {boolean} [firstSlot=false] - Whether the video is in the first slot of the gallery. It's optional
 * @returns {Function} IntersectionObserver callback.
 */
export const generateVideoObserverCallback = (firstSlot = false) => {
  return (entries, observer) => {
    entries.forEach((entry) => {
      const videoElement = entry.target.querySelector('video');
      const videoSources = entry.target.querySelectorAll('source');

      if (!videoElement || !videoSources.length) {
        return console.error('There was no video element found');
      }

      if (entry.isIntersecting) {
        // Intersect video element
        !videoElement.hasAttribute('data-loaded') &&
          videoSources.forEach((videoSource) => {
            const targetUrl = videoSource.dataset.src;
            if (!targetUrl) return;
            // Pipe through interceptVideoUrl/applyVideoFetchPrefix so the Cloudinary fetch prefix
            // is applied. Without this, scroll-into-view (mobile or non-hover)
            // serves the raw Shopify URL — the prefix only got applied on
            // hover via index.js:537.

            if (videoSource.src !== targetUrl) {
              const placement = videoSource.dataset.placement;
              videoSource.src = applyVideoFetchPrefix(targetUrl, placement);
              delete videoSource.dataset['src'];
            }
          });
        !videoElement.hasAttribute('data-loaded') && videoElement.load();
        // Preload images for non-first slot videos when they come into view, to prioritize bandwidth for the video in the first slot
        !videoElement.hasAttribute('data-loaded') &&
          Number(entry.target?.id?.split('_').at(-1)) > 0 &&
          preloadImages(entry.target);
        videoElement.setAttribute('data-loaded', true);
        videoElement.play();
        firstSlot && observer.disconnect();
      } else {
        videoElement.pause();
      }
    });
  };
};

/**
 * Generates options for the IntersectionObserver used for videos.
 *
 * @param {HTMLElement|null} rootElement - The root element for the observer.
 * @returns {Object} IntersectionObserver options.
 */
export const generateVideoObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.1, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};
